import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import ScrollToHash from "@/components/ScrollToHash";
import Analytics from "@/components/Analytics";
import { installClickTracking } from "@/lib/track";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import RequireFeature from "@/components/RequireFeature";
import OfflineBanner from "@/components/OfflineBanner";
import CheckoutReturn from "@/components/CheckoutReturn";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";

// --- Eagerly loaded (auth entry points + tiny fallback) ---
// These need to be reachable without a network round-trip for the chunk,
// because they are often the first route a user lands on.
import Login from "./pages/Login.tsx";
import Register from "./pages/Register.tsx";
import AuthCallback from "./pages/AuthCallback.tsx";
import ForgotPassword from "./pages/ForgotPassword.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import NotFound from "./pages/NotFound.tsx";

// --- Lazy-loaded public pages ---
const Index = lazy(() => import("./pages/Index.tsx"));
const GetStarted = lazy(() => import("./pages/GetStarted.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const OnboardingWizard = lazy(() => import("./pages/onboarding/OnboardingWizard.tsx"));

// --- Blog and Results pages ---
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex.tsx"));
const BlogPost = lazy(() => import("./pages/blog/BlogPost.tsx"));
const ResultsIndex = lazy(() => import("./pages/results/ResultsIndex.tsx"));
const ResultsPost = lazy(() => import("./pages/results/ResultsPost.tsx"));

// --- Lazy-loaded feature pages ---
const PnLAccounting = lazy(() => import("./pages/features/PnLAccounting.tsx"));
const Inventory = lazy(() => import("./pages/features/Inventory.tsx"));
const AutoReorder = lazy(() => import("./pages/features/AutoReorder.tsx"));
const InvoiceUpload = lazy(() => import("./pages/features/InvoiceUpload.tsx"));
const Payroll = lazy(() => import("./pages/features/Payroll.tsx"));
const InsightsAgent = lazy(() => import("./pages/features/InsightsAgent.tsx"));
const Performance = lazy(() => import("./pages/features/Performance.tsx"));
const LoyaltySMS = lazy(() => import("./pages/features/LoyaltySMS.tsx"));
const DynamicPricing = lazy(() => import("./pages/features/DynamicPricing.tsx"));
const AIReviews = lazy(() => import("./pages/features/AIReviews.tsx"));
const Catering = lazy(() => import("./pages/features/Catering.tsx"));

// Cold-outreach landing pages (fed by PREP CRM 2.0's campaigns)
const LaborCost = lazy(() => import("./pages/lp/LaborCost.tsx"));
const Savings = lazy(() => import("./pages/lp/Savings.tsx"));
const MarginWaste = lazy(() => import("./pages/lp/MarginWaste.tsx"));
const SquareLanding = lazy(() => import("./pages/pos/Square.tsx"));
const CloverLanding = lazy(() => import("./pages/pos/Clover.tsx"));

// SEO solutions matrix (feature x vertical, fed by PREP CRM 2.0's campaigns) —
// one dynamic route backs every /solutions/* page; see src/lib/solutions/.
const SolutionPage = lazy(() => import("./pages/solutions/SolutionPage.tsx"));
// Human/crawler directory of every solutions page — see SolutionsIndex.tsx.
const SolutionsIndex = lazy(() => import("./pages/solutions/SolutionsIndex.tsx"));
const CustomerIntelligence = lazy(() => import("./pages/features/CustomerIntelligence.tsx"));
const ChannelPnL = lazy(() => import("./pages/features/ChannelPnL.tsx"));
const AnalyticsOffers = lazy(() => import("./pages/features/AnalyticsOffers.tsx"));
const RestaurantWebsite = lazy(() => import("./pages/features/RestaurantWebsite.tsx"));
const MarketIntelligencePage = lazy(() => import("./pages/features/MarketIntelligence.tsx"));
const MetaAds = lazy(() => import("./pages/features/MetaAds.tsx"));

// --- Phase 3 public merchant landing pages (no auth) ---
const MerchantJoinLanding = lazy(() => import("./pages/public/MerchantJoinLanding.tsx"));
const MerchantClaimCode = lazy(() => import("./pages/public/MerchantClaimCode.tsx"));
const MerchantJoinedSuccess = lazy(() => import("./pages/public/MerchantJoinedSuccess.tsx"));

// --- Lazy-loaded mock portal (public demo) ---
const PortalLayout = lazy(() => import("./components/portal/PortalLayout.tsx"));
const DashboardTab = lazy(() => import("./pages/portal/DashboardTab.tsx"));
const InsightsAgentTab = lazy(() => import("./pages/portal/InsightsAgentTab.tsx"));
const PnlTab = lazy(() => import("./pages/portal/PnlTab.tsx"));
const InventoryTab = lazy(() => import("./pages/portal/InventoryTab.tsx"));
const PerformanceTab = lazy(() => import("./pages/portal/PerformanceTab.tsx"));
const PayrollTab = lazy(() => import("./pages/portal/PayrollTab.tsx"));
const LoyaltyTab = lazy(() => import("./pages/portal/LoyaltyTab.tsx"));
const DynamicPricingTab = lazy(() => import("./pages/portal/DynamicPricingTab.tsx"));
const BillingTab = lazy(() => import("./pages/portal/BillingTab.tsx"));
const ChannelsTab = lazy(() => import("./pages/portal/ChannelsTab.tsx"));
const CustomersTab = lazy(() => import("./pages/portal/CustomersTab.tsx"));
const SettingsTab = lazy(() => import("./pages/portal/SettingsTab.tsx"));
const ReviewsTab = lazy(() => import("./pages/portal/ReviewsTab.tsx"));
const CateringTab = lazy(() => import("./pages/portal/CateringTab.tsx"));
const WebsiteTab = lazy(() => import("./pages/portal/WebsiteTab.tsx"));
const MarketIntelligenceTab = lazy(() => import("./pages/portal/MarketIntelligenceTab.tsx"));

// --- Lazy-loaded production portal (authenticated) ---
const AppLayout = lazy(() => import("./layouts/AppLayout.tsx"));
const AppDashboard = lazy(() => import("./pages/app/AppDashboard.tsx"));
const AppInsights = lazy(() => import("./pages/app/AppInsights.tsx"));
const AppPnL = lazy(() => import("./pages/app/AppPnL.tsx"));
const AppAccounting = lazy(() => import("./pages/app/AppAccounting.tsx"));
const AppInventory = lazy(() => import("./pages/app/AppInventory.tsx"));
const AppPerformance = lazy(() => import("./pages/app/AppPerformance.tsx"));
const AppPayroll = lazy(() => import("./pages/app/AppPayroll.tsx"));
const AppPricing = lazy(() => import("./pages/app/AppPricing.tsx"));
const MenuManagement = lazy(() => import("./pages/app/MenuManagement.tsx"));
const AppBilling = lazy(() => import("./pages/app/AppBilling.tsx"));
const AppChannels = lazy(() => import("./pages/app/AppChannels.tsx"));
const AppCustomers = lazy(() => import("./pages/app/AppCustomers.tsx"));
const AppSettings = lazy(() => import("./pages/app/AppSettings.tsx"));
const AppNotifications = lazy(() => import("./pages/app/AppNotifications.tsx"));
const AppNotificationSettings = lazy(() => import("./pages/app/AppNotificationSettings.tsx"));
const AppSettingsPermits = lazy(() => import("./pages/app/AppSettingsPermits.tsx"));
const AppReviews = lazy(() => import("./pages/app/AppReviews.tsx"));
const AppCatering = lazy(() => import("./pages/app/AppCatering.tsx"));
const AppMarketIntelligence = lazy(() => import("./pages/app/AppMarketIntelligence.tsx"));
const AppWebsite = lazy(() => import("./pages/app/AppWebsite.tsx"));
const CampaignDetailPage = lazy(() => import("./pages/app/CampaignDetailPage.tsx"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

function SmartHome() {
  const { isAuthenticated, isOnboarded, loading } = useAuth();
  if (loading) return null;
  // Authenticated but not yet checked out → resume onboarding, don't drop them
  // on the dashboard (the paywall bypass: open a new tab to the root and land
  // in the app without paying).
  if (isAuthenticated) {
    return <Navigate to={isOnboarded ? "/app/dashboard" : "/onboarding/step/details"} replace />;
  }
  return <Index />;
}

// Single Suspense boundary for every lazy chunk. Keeps the fallback
// consistent across public, portal, and /app routes.
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

const App = () => {
  // Trial / setup-call / sample-dashboard click events (no-op until tracking is switched on).
  useEffect(() => installClickTracking(), []);
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <OfflineBanner />
      <BrowserRouter>
        <ScrollToHash />
        <Analytics />
        <AuthProvider>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<SmartHome />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/pricing" element={<Navigate to="/#plans" replace />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/get-started" element={<GetStarted />} />
              <Route path="/onboarding/step/:stepId" element={<OnboardingWizard />} />
              {/* Stripe Checkout return URL (STRIPE_SUCCESS_URL / STRIPE_CANCEL_URL) */}
              <Route path="/billing" element={<CheckoutReturn />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />

              {/* Phase 3 — public merchant landing (QR acquisition, no auth) */}
              <Route path="/m/:slug/join" element={<MerchantJoinLanding />} />
              <Route path="/m/:slug/c/:token" element={<MerchantClaimCode />} />
              <Route path="/m/:slug/joined" element={<MerchantJoinedSuccess />} />

              {/* Feature pages */}
              <Route path="/features/pnl-accounting" element={<PnLAccounting />} />
              <Route path="/features/inventory" element={<Inventory />} />
              <Route path="/features/auto-reorder" element={<AutoReorder />} />
              <Route path="/features/invoice-upload" element={<InvoiceUpload />} />
              <Route path="/features/payroll" element={<Payroll />} />
              <Route path="/features/insights-agent" element={<InsightsAgent />} />
              <Route path="/features/performance" element={<Performance />} />
              <Route path="/features/loyalty-sms" element={<LoyaltySMS />} />
              <Route path="/features/dynamic-pricing" element={<DynamicPricing />} />
              <Route path="/features/ai-reviews" element={<AIReviews />} />
              <Route path="/features/catering" element={<Catering />} />
              <Route path="/features/customer-intelligence" element={<CustomerIntelligence />} />
              <Route path="/features/channel-pnl" element={<ChannelPnL />} />
              <Route path="/features/analytics-offers" element={<AnalyticsOffers />} />
              <Route path="/features/restaurant-website" element={<RestaurantWebsite />} />
              <Route path="/features/market-intelligence" element={<MarketIntelligencePage />} />
              <Route path="/features/meta-ads" element={<MetaAds />} />

              {/* Cold-outreach landing pages */}
              <Route path="/savings" element={<Savings />} />
              <Route path="/lp/labor-cost" element={<LaborCost />} />
              <Route path="/lp/margin-waste" element={<MarginWaste />} />
              <Route path="/square" element={<SquareLanding />} />
              <Route path="/clover" element={<CloverLanding />} />

              {/* SEO solutions matrix — feature x vertical, one dynamic route.
                  Add pages via src/lib/solutions/data/, not here. */}
              <Route path="/solutions" element={<SolutionsIndex />} />
              <Route path="/solutions/:featureSlug/:verticalSlug" element={<SolutionPage />} />

              {/* Mock portal — public demo, no auth */}
              <Route path="/portal" element={<PortalLayout />}>
                <Route index element={<DashboardTab />} />
                <Route path="dashboard" element={<DashboardTab />} />
                <Route path="insights" element={<InsightsAgentTab />} />
                <Route path="pnl" element={<PnlTab />} />
                <Route path="inventory" element={<InventoryTab />} />
                <Route path="performance" element={<PerformanceTab />} />
                <Route path="catering" element={<CateringTab />} />
                <Route path="payroll" element={<PayrollTab />} />
                <Route path="loyalty" element={<LoyaltyTab />} />
                <Route path="pricing" element={<DynamicPricingTab />} />
                <Route path="billing" element={<BillingTab />} />
                <Route path="channels" element={<ChannelsTab />} />
                <Route path="customers" element={<CustomersTab />} />
                <Route path="reviews" element={<ReviewsTab />} />
                <Route path="market-intelligence" element={<MarketIntelligenceTab />} />
                <Route path="website" element={<WebsiteTab />} />
                <Route path="settings" element={<SettingsTab />} />
              </Route>

              {/* Production portal — authenticated */}
              <Route element={<ProtectedRoute />}>
                <Route path="/app" element={<AppLayout />}>
                  <Route index element={<ErrorBoundary><AppDashboard /></ErrorBoundary>} />
                  <Route path="dashboard" element={<ErrorBoundary><AppDashboard /></ErrorBoundary>} />
                  <Route path="insights" element={<ErrorBoundary><AppInsights /></ErrorBoundary>} />
                  <Route path="pnl" element={<ErrorBoundary><AppPnL /></ErrorBoundary>} />
                  <Route path="accounting" element={<ErrorBoundary><AppAccounting /></ErrorBoundary>} />
                  <Route path="inventory" element={<ErrorBoundary><AppInventory /></ErrorBoundary>} />
                  <Route path="performance" element={<ErrorBoundary><RequireFeature feature="performance_menu"><AppPerformance /></RequireFeature></ErrorBoundary>} />
                  <Route path="catering" element={<ErrorBoundary><RequireFeature feature="catering"><AppCatering /></RequireFeature></ErrorBoundary>} />
                  <Route path="payroll" element={<ErrorBoundary><AppPayroll /></ErrorBoundary>} />
                  <Route path="loyalty" element={<ErrorBoundary><RequireFeature feature="customer_crm"><AppCustomers view="marketing" /></RequireFeature></ErrorBoundary>} />
                  <Route path="pricing" element={<ErrorBoundary><RequireFeature feature="dynamic_pricing"><AppPricing /></RequireFeature></ErrorBoundary>} />
                  <Route path="menu" element={<ErrorBoundary><RequireFeature feature="performance_menu"><MenuManagement /></RequireFeature></ErrorBoundary>} />
                  <Route path="billing" element={<ErrorBoundary><AppBilling /></ErrorBoundary>} />
                  <Route path="channels" element={<ErrorBoundary><RequireFeature feature="channel_pnl"><AppChannels /></RequireFeature></ErrorBoundary>} />
                  <Route path="customers" element={<ErrorBoundary><RequireFeature feature="customer_crm"><AppCustomers view="customers" /></RequireFeature></ErrorBoundary>} />
                  <Route path="loyalty/campaigns/:campaignId" element={<ErrorBoundary><RequireFeature feature="customer_crm"><CampaignDetailPage /></RequireFeature></ErrorBoundary>} />
                  <Route path="reviews" element={<ErrorBoundary><RequireFeature feature="reviews"><AppReviews /></RequireFeature></ErrorBoundary>} />
                  <Route path="market-intelligence" element={<ErrorBoundary><RequireFeature feature="market_intelligence"><AppMarketIntelligence /></RequireFeature></ErrorBoundary>} />
                  <Route path="website" element={<ErrorBoundary><RequireFeature feature="restaurant_website"><AppWebsite /></RequireFeature></ErrorBoundary>} />
                  <Route path="settings" element={<ErrorBoundary><AppSettings /></ErrorBoundary>} />
                  <Route path="notifications" element={<ErrorBoundary><AppNotifications /></ErrorBoundary>} />
                  <Route path="settings/notifications" element={<ErrorBoundary><AppNotificationSettings /></ErrorBoundary>} />
                  <Route path="settings/permits" element={<ErrorBoundary><AppSettingsPermits /></ErrorBoundary>} />
                </Route>
              </Route>

              {/* Blog and Results routes */}
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/results" element={<ResultsIndex />} />
              <Route path="/results/:slug" element={<ResultsPost />} />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;
